/**
 * The AI summary content THIS session has already generated, kept alive
 * across an in-page remount (SE-8594).
 *
 * `useAISummaryLanguage` (`src/features/player-detail/hooks/useAISummary.ts`)
 * arbitrates which language a summary card resolves to as
 * `override → profile → restored` (SE-6511, deliberately: a one-off
 * per-summary language pick must not act like it silently became the
 * account default). That arbitration is correct for what language a NEW
 * generation, the picker, or dictation should use — but the Overview tab
 * unmounts `PlayerSummaryCard` on every tab switch (`PlayerPageTabContent
 * Container` only conditionally renders it), which wipes the card's local
 * `summaryContent` state. On remount, if the account's profile default
 * differs from the language the summary was actually generated in, the
 * AsyncStorage cache lookup misses under the arbitrated language and the
 * card falls back to its empty state — forcing the user to regenerate a
 * summary they already paid the wait time for, even though they never left
 * the tab.
 *
 * This var is a second, narrower answer kept deliberately separate from that
 * arbitration: "did THIS browser session already generate a summary for this
 * player, regardless of what language a NEW generation would use." It never
 * changes `selectedLanguage`, the picker, or dictation — only what content a
 * cache-miss falls back to displaying.
 * @module stores/aiSummarySession
 */
import { makeVar } from "@apollo/client";

/** One session-generated summary, addressed by {@link sessionSummaryKey}. */
export interface SessionGeneratedSummary {
  /** `${cachePrefix}|${playerId}` — see {@link sessionSummaryKey}. */
  readonly key: string;
  /** The cleaned summary text as generation produced it. */
  readonly content: string;
  /** The cache timestamp `saveSummaryToCache` returned, or null. */
  readonly lastUpdated: string | null;
}

/**
 * How many session-generated summaries are remembered at once.
 *
 * Mirrors `PLAYER_TAB_MEMORY_LIMIT` (`src/stores/player.ts`): a session
 * realistically revisits a handful of players' summaries in one working
 * pass, and bounding the var keeps a long session from growing it
 * unboundedly with every player it ever generated a summary for.
 */
export const SESSION_SUMMARY_MEMORY_LIMIT = 20;

/**
 * Every summary this session has generated, most recently generated first.
 *
 * Treat the value as immutable, exactly like `playerTabMemoryVar` — writers
 * replace it with a new array (see {@link rememberSessionSummary}); mutating
 * it in place would leave `useReactiveVar` (were anything to subscribe)
 * holding the same reference and nothing would re-render.
 */
export const sessionGeneratedSummariesVar = makeVar<
  readonly SessionGeneratedSummary[]
>([]);

/**
 * The key a session-generated summary is addressed by.
 *
 * Scoped by cache prefix AND player, mirroring `useAISummary`'s own cache
 * key: the player-summary and scouting-summary caches for the same player
 * must never answer for each other.
 * @param cachePrefix - Which cache namespace (player vs scouting summary)
 * @param playerId - The player the summary belongs to
 * @returns The composite key
 */
export const sessionSummaryKey = (
  cachePrefix: string,
  playerId: string
): string => `${cachePrefix}|${playerId}`;

/**
 * The memory that results from recording a summary for `key`.
 *
 * Pure. Returns a new array with that target at the front, any earlier entry
 * for it removed, and the oldest entries dropped past the limit.
 * @param memory - The current memory
 * @param entry - The summary being recorded
 * @returns The new memory
 */
export const rememberSessionSummary = (
  memory: readonly SessionGeneratedSummary[],
  entry: SessionGeneratedSummary
): readonly SessionGeneratedSummary[] =>
  [entry, ...memory.filter(existing => existing.key !== entry.key)].slice(
    0,
    SESSION_SUMMARY_MEMORY_LIMIT
  );

/**
 * Records that this session generated `content` for `playerId` under
 * `cachePrefix`.
 * @param cachePrefix - Which cache namespace (player vs scouting summary)
 * @param playerId - The player the summary belongs to
 * @param content - The cleaned summary text
 * @param lastUpdated - The cache timestamp `saveSummaryToCache` returned
 */
export const recordSessionGeneratedSummary = (
  cachePrefix: string,
  playerId: string,
  content: string,
  lastUpdated: string | null
): void => {
  sessionGeneratedSummariesVar(
    rememberSessionSummary(sessionGeneratedSummariesVar(), {
      key: sessionSummaryKey(cachePrefix, playerId),
      content,
      lastUpdated,
    })
  );
};

/**
 * The summary this session generated for `playerId` under `cachePrefix`, if
 * any.
 *
 * Pure derivation, with no write — safe to call from inside an async effect
 * callback, not only from a render.
 * @param memory - The current memory
 * @param cachePrefix - Which cache namespace (player vs scouting summary)
 * @param playerId - The player to look up
 * @returns The recorded summary, or undefined if this session never
 * generated one for that target
 */
export const resolveSessionGeneratedSummary = (
  memory: readonly SessionGeneratedSummary[],
  cachePrefix: string,
  playerId: string
): SessionGeneratedSummary | undefined => {
  const key = sessionSummaryKey(cachePrefix, playerId);
  return memory.find(entry => entry.key === key);
};
